import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { UploadCloud, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { API } from '../App';

const enconOrange = '#ee7203';

/**
 * Upload modal for Settings > Pricing > Rebates. Accepts the "Rebate Summary"
 * spreadsheet (Product Code / Product Description / Rebate % / Variance if
 * any? columns), groups it by each code's Product Analysis Group (via the
 * Stock Database), and auto-attaches the resulting % to any matching Pricing
 * category — safe to re-run whenever a fresh rebate summary needs merging in.
 */
const RebateUploadModal = ({ isOpen, onClose, onUploaded }) => {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const reset = () => { setResult(null); setUploading(false); setDragOver(false); };
  const handleClose = () => { reset(); onClose(); };

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`${API}/pricing/rebates/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult({ ok: true, ...res.data, filename: file.name });
      toast.success(`Processed ${res.data.products_processed} products — ${res.data.categories_auto_populated} Pricing categories auto-populated`);
      onUploaded && onUploaded(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Upload failed — check the sheet has 'Product Code' and 'Rebate %' columns";
      setResult({ ok: false, message: detail, filename: file.name });
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) doUpload(file);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-white max-w-lg" data-testid="rebate-upload-modal">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2" style={{ color: enconOrange }}>
            <UploadCloud className="w-6 h-6" />
            Upload Rebate Summary
          </DialogTitle>
          <DialogDescription>
            Drop the "Rebate Percentage Summary" spreadsheet — each product code is grouped by its
            Product Analysis Group, and the resulting % is auto-attached to any matching Pricing
            category (including ones added later for a new supplier/group).
          </DialogDescription>
        </DialogHeader>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
          data-testid="rebate-upload-dropzone"
          className="mt-2 flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-xl cursor-pointer transition-colors"
          style={{
            borderColor: dragOver ? enconOrange : '#d1d5db',
            background: dragOver ? `${enconOrange}0d` : '#fafafa',
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => { doUpload(e.target.files?.[0]); e.target.value = ''; }}
            data-testid="rebate-upload-file-input"
          />
          {uploading ? (
            <>
              <Loader2 className="w-9 h-9 mb-2 animate-spin" style={{ color: enconOrange }} />
              <p className="text-sm font-medium text-gray-500">Processing…</p>
            </>
          ) : (
            <>
              <UploadCloud className="w-9 h-9 mb-2" style={{ color: enconOrange }} />
              <p className="text-sm text-gray-600"><span className="font-semibold">Click to browse</span> or drag &amp; drop</p>
              <p className="text-xs text-gray-400 mt-1">.xlsx rebate summary</p>
            </>
          )}
        </div>

        {result && (
          <div
            data-testid="rebate-upload-result"
            className={`flex items-start gap-3 p-3 rounded-xl border text-sm ${result.ok ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
          >
            {result.ok ? (
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            )}
            {result.ok ? (
              <div className="text-green-700">
                <p>{result.products_processed} products processed → {result.groups_created} new groups, {result.groups_updated} updated.</p>
                <p className="mt-1 font-semibold">{result.categories_auto_populated} Pricing categories auto-populated with a rebate.</p>
                {result.unmatched_to_stock_db > 0 && <p className="mt-1 text-amber-700">{result.unmatched_to_stock_db} product code(s) weren't found in the Stock Database.</p>}
              </div>
            ) : (
              <p className="text-red-600">{result.message}</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RebateUploadModal;
